import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import trainingLibraryService, { TrainingVideo } from '../services/trainingLibraryService';
import MenuButton from '../components/MenuButton';

const STATIC_CATEGORIES = [
    {
        key: 'swimming',
        label: 'Swimming Technique',
        desc: 'Drills and exercises to improve your stroke efficiency and power in the water.',
        icon: 'fa-person-swimming',
        iconBg: 'bg-cyan-100 text-cyan-600 group-hover:bg-cyan-600 group-hover:text-white',
        badge: 'bg-cyan-50 text-cyan-700',
        link: 'text-cyan-600',
    },
    {
        key: 'strength',
        label: 'Strength & Conditioning',
        desc: 'Gym workouts specifically designed for triathletes to build functional strength.',
        icon: 'fa-dumbbell',
        iconBg: 'bg-orange-100 text-orange-600 group-hover:bg-orange-600 group-hover:text-white',
        badge: 'bg-orange-50 text-orange-700',
        link: 'text-orange-600',
    },
    {
        key: 'general',
        label: 'Stretching & Mobility',
        desc: 'Essential stretching routines for recovery and injury prevention.',
        icon: 'fa-person-walking',
        iconBg: 'bg-lime-100 text-lime-600 group-hover:bg-lime-600 group-hover:text-white',
        badge: 'bg-lime-50 text-lime-700',
        link: 'text-lime-600',
    },
];

const VIDEO_FILTERS = ['All', 'Swimming', 'Strength', 'Running', 'Cycling', 'General'];

const extractYouTubeId = (url: string): string | null => {
    const patterns = [
        /(?:youtube\.com\/watch\?v=)([^&\s]+)/,
        /(?:youtu\.be\/)([^?\s]+)/,
        /(?:youtube\.com\/embed\/)([^?\s]+)/,
    ];
    for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match?.[1]) return match[1];
    }
    return null;
};

const normalizeCategory = (category: string | null): string => category?.toLowerCase() || 'general';

const TrainingLibrary: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [videos, setVideos] = useState<TrainingVideo[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [activeFilter, setActiveFilter] = useState('All');

    useEffect(() => {
        let mounted = true;
        const fetchVideos = async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await trainingLibraryService.getPublishedVideos();
                if (mounted) setVideos(data);
            } catch {
                if (mounted) setError('Unable to load training videos right now.');
            } finally {
                if (mounted) setLoading(false);
            }
        };
        fetchVideos();
        return () => { mounted = false; };
    }, []);

    // Count videos per static category
    const videoCounts = useMemo(() => {
        const counts: Record<string, number> = {};
        videos.forEach((v) => {
            const key = normalizeCategory(v.category);
            counts[key] = (counts[key] || 0) + 1;
        });
        return counts;
    }, [videos]);

    const filteredVideos = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        return videos.filter((v) => {
            const cat = normalizeCategory(v.category);
            const catOk = activeFilter === 'All' || cat === activeFilter.toLowerCase();
            const searchOk = !query || `${v.title} ${v.description || ''} ${cat}`.toLowerCase().includes(query);
            return catOk && searchOk;
        });
    }, [videos, activeFilter, searchQuery]);

    return (
        <div className="min-h-screen bg-slate-100 flex flex-col">
            {/* Header */}
            <header className="h-24 bg-gradient-to-r from-cyan-500 to-blue-500 border-b border-cyan-600 flex items-center justify-between px-4 md:px-8 shadow-md sticky top-0 z-20 relative overflow-hidden">
                <div className="absolute -right-20 -top-20 w-64 h-64 bg-white/10 rounded-full blur-3xl pointer-events-none"></div>
                <div className="relative z-10 flex items-center gap-3 md:gap-4 min-w-0">
                    <MenuButton />
                    <div className="hidden sm:flex w-10 h-10 rounded-full bg-white/10 items-center justify-center text-white border border-white/20 flex-shrink-0">
                        <i className="fa-solid fa-video text-lg"></i>
                    </div>
                    <div className="min-w-0">
                        <h1 className="text-base md:text-xl font-bold text-white tracking-tight truncate">{t('trainingLibrary.title')}</h1>
                        <p className="hidden md:block text-cyan-100 text-xs font-medium">{t('trainingLibrary.subtitle')}</p>
                    </div>
                </div>
                {/* Search */}
                <div className="hidden md:flex relative max-w-xs w-full ml-auto">
                    <i className="fa-solid fa-search absolute left-3.5 top-1/2 -translate-y-1/2 text-cyan-100 text-sm"></i>
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={t('trainingLibrary.searchPlaceholder')}
                        className="w-full bg-white/10 border border-white/20 rounded-full pl-10 pr-4 py-2 text-white placeholder-cyan-100 focus:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/30 transition-all text-sm"
                    />
                </div>
            </header>

            <div className="flex-1 overflow-y-auto p-4 lg:p-8">
                <div className="flex flex-col gap-8 pb-20">

                    {/* Yellow Banner */}
                    <div className="hidden bg-gradient-to-r from-yellow-400 to-amber-400 rounded-xl p-6 shadow-lg flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center text-yellow-900">
                                <i className="fa-solid fa-lightbulb text-2xl"></i>
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-yellow-950">Training Examples &amp; Guides</h2>
                                <p className="text-yellow-900/80 font-medium text-sm">Browse our comprehensive collection of workout tables and video tutorials.</p>
                            </div>
                        </div>
                        <button className="bg-white/20 hover:bg-white/30 text-yellow-950 px-4 py-2 rounded-lg font-bold transition-colors text-sm whitespace-nowrap ml-4">
                            View All Categories
                        </button>
                    </div>

                    {/* Category Cards */}
                    <div className="hidden grid-cols-1 md:grid-cols-3 gap-6">
                        {STATIC_CATEGORIES.map((cat) => {
                            const count = videoCounts[cat.key] ?? 0;
                            return (
                                <div
                                    key={cat.key}
                                    onClick={() => setActiveFilter(cat.key === 'general' ? 'All' : cat.key.charAt(0).toUpperCase() + cat.key.slice(1))}
                                    className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 group cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
                                >
                                    <div className="flex items-start justify-between mb-4">
                                        <div className={`w-14 h-14 rounded-xl flex items-center justify-center transition-colors ${cat.iconBg}`}>
                                            <i className={`fa-solid ${cat.icon} text-2xl`}></i>
                                        </div>
                                        {count > 0 && (
                                            <span className={`text-xs font-bold px-2 py-1 rounded ${cat.badge}`}>{count} Videos</span>
                                        )}
                                    </div>
                                    <h3 className="text-lg font-bold text-slate-800 mb-2">{cat.label}</h3>
                                    <p className="text-slate-500 text-sm mb-4">{cat.desc}</p>
                                    <span className={`text-sm font-semibold flex items-center gap-2 ${cat.link}`}>
                                        Browse Videos <i className="fa-solid fa-arrow-right"></i>
                                    </span>
                                </div>
                            );
                        })}
                    </div>

                    {/* Latest Uploads */}
                    <div className="flex flex-col gap-6">
                        <div className="flex items-center justify-between border-b border-slate-200 pb-4">
                            <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                                <i className="fa-brands fa-youtube text-red-600"></i> {t('trainingLibrary.latestUploads')}
                            </h3>
                            <div className="flex gap-2">
                                {VIDEO_FILTERS.map((f) => (
                                    <button
                                        key={f}
                                        onClick={() => setActiveFilter(f)}
                                        className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                                            activeFilter === f
                                                ? 'bg-slate-100 text-slate-600'
                                                : 'bg-white text-slate-500 border border-slate-200 hover:bg-slate-50'
                                        }`}
                                    >
                                        {t(`trainingLibrary.filters.${f.toLowerCase()}`)}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {loading && (
                            <div className="flex items-center justify-center py-24 text-slate-400 gap-3">
                                <i className="fa-solid fa-spinner fa-spin text-2xl"></i>
                                <span className="text-lg">{t('trainingLibrary.loading')}</span>
                            </div>
                        )}

                        {!loading && error && (
                            <div className="flex items-center justify-center py-24 gap-3 text-red-400">
                                <i className="fa-solid fa-circle-exclamation text-2xl"></i>
                                <span>{t('trainingLibrary.errorLoad')}</span>
                            </div>
                        )}

                        {!loading && !error && filteredVideos.length === 0 && (
                            <div className="flex flex-col items-center justify-center py-24 text-slate-400 gap-3">
                                <i className="fa-solid fa-film text-4xl"></i>
                                <p className="text-lg font-medium">{t('trainingLibrary.noVideos')}</p>
                                {searchQuery && <p className="text-sm">{t('trainingLibrary.noVideosHint')}</p>}
                            </div>
                        )}

                        {!loading && !error && filteredVideos.length > 0 && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                {filteredVideos.map((video) => {
                                    const youtubeId = extractYouTubeId(video.youtubeUrl);
                                    const thumbnail = youtubeId
                                        ? `https://img.youtube.com/vi/${youtubeId}/mqdefault.jpg`
                                        : null;
                                    const initial = video.title?.charAt(0)?.toUpperCase() ?? 'W';

                                    return (
                                        <div
                                            key={video.id}
                                            onClick={() => navigate(`/training-library/${video.id}`)}
                                            className="bg-white rounded-xl overflow-hidden shadow-sm border border-slate-200 group hover:shadow-md transition-shadow cursor-pointer"
                                        >
                                            {/* Thumbnail */}
                                            <div className="relative aspect-video bg-slate-900">
                                                {thumbnail ? (
                                                    <img
                                                        src={thumbnail}
                                                        alt={video.title}
                                                        className="w-full h-full object-cover opacity-80 group-hover:opacity-60 transition-opacity"
                                                    />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center text-slate-600">
                                                        <i className="fa-solid fa-video text-4xl"></i>
                                                    </div>
                                                )}
                                                {/* Red play button */}
                                                <div className="absolute inset-0 flex items-center justify-center opacity-80 group-hover:opacity-100 transition-opacity duration-300">
                                                    <div className="w-14 h-14 bg-red-600 rounded-full flex items-center justify-center shadow-lg shadow-red-600/30 transform group-hover:scale-110 transition-transform">
                                                        <i className="fa-solid fa-play text-white ml-1 text-xl"></i>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Content */}
                                            <div className="p-4">
                                                <div className="flex items-start gap-3">
                                                    <div className="w-8 h-8 rounded-full bg-slate-100 flex-shrink-0 flex items-center justify-center text-slate-500 font-bold text-xs">
                                                        {initial}
                                                    </div>
                                                    <div>
                                                        <h4 className="font-bold text-slate-800 line-clamp-1 group-hover:text-cyan-600 transition-colors text-sm">
                                                            {video.title}
                                                        </h4>
                                                        <p className="text-xs text-slate-500 mt-1 line-clamp-1">
                                                            {video.description || t('trainingLibrary.noDescription')}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TrainingLibrary;
